import React, { useEffect, useState } from 'react'
import { makeStyles } from '@paintscout/ui'
import { useUserPermissions } from '@paintscout/ui-business'
import { useHistory, useLocation } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { Sentry } from 'src/util/appSentry'
import { useSearchDealsLazyQuery } from '@paintscout/api'
import type { SearchDealsQuery } from '@paintscout/api'
import type { DealUrlQuery } from 'src/components/common/DealSearchBar'
import DealSearchBar from 'src/components/common/DealSearchBar'
import DealTable from 'src/components/common/DealTable'
import type { ValueFilter } from 'json-to-lucene'
import { jsonToLucene } from 'json-to-lucene'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import { getFiltersFromLocation, getQueryFromLocation } from 'src/components/common/QuoteSearchBar'

const useStyles = makeStyles((theme) => ({
  root: {},
  dealTableCard: {
    border: `1px solid ${theme.palette.divider}`,
    overflowX: 'auto',
    transform: 'scaleY(-1)',
    '& > *': {
      transform: 'scaleY(-1)'
    },
    // flip the box shadow
    boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)'
  }
}))

interface ListViewProps {
  pipeline: string
}

export function ListView(props: ListViewProps) {
  const { pipeline } = props
  const history = useHistory()
  const classes = useStyles()

  const limit = 20
  const location = useLocation<DealUrlQuery>()
  const { enqueueSnackbar } = useSnackbar()
  const [page, setPage] = useState(location.state?.page ?? 1)
  const [bookmark, setBookmark] = useState(null)
  const [queryState, setQueryState] = useState(getQueryFromLocation(location))
  const { permissionToEditAllDeals } = useUserPermissions()
  const [queryText, setQueryText] = useState(getUrlQuery<DealUrlQuery>({ location }).search || '')
  const [filters, setFilters] = useState(getFiltersFromLocation(location, true))
  const urlParams = getUrlQuery<DealUrlQuery>({ location })

  const [sort, setSort] = useState(urlParams.sort || '') // column sort

  const getSort = (sort: string, _queryState: string) => {
    const secondarySort = '-updated'
    const newSort = [sort, secondarySort].filter(Boolean)
    if (newSort.length === 1) return newSort[0]
    return newSort
  }

  // console.log('queryState', queryState)
  // console.log('queryText', queryText)
  // console.log('filters', filters)
  // console.log('sort: ', sort)
  // console.log('pipeline: ', pipeline)
  // console.log('urlParams: ', urlParams)

  const [search, { loading, data, error, fetchMore, updateQuery }] = useSearchDealsLazyQuery({
    // if we're on page 1 we'll also refetch to get latest results
    fetchPolicy: page === 1 ? (location?.state?.fullLoad ? 'no-cache' : 'cache-and-network') : 'cache-first',
    onCompleted: (newData) => {
      if (newData.searchDeals) {
        setBookmark(newData.searchDeals.bookmark)
      }
    }
  })

  useEffect(() => {
    // limit * page is to fetch all pages up to the current on
    if (pipeline) {
      const query = handlePipelineQuery({ pipeline, query: queryText, filters })
      setQueryState(query)
      search({
        variables: {
          query,
          sort: getSort(sort, query),
          limit
        }
      })
    }
  }, [pipeline])

  useEffect(() => {
    history.replace({
      ...history.location,
      state: { page }
    })
  }, [page])

  // show error toast
  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
      enqueueSnackbar(`Unable to search Deals`, { variant: 'error' })
    }
  }, [error])

  function removeRowsFromQuery(ids: string[]) {
    updateQuery((cache: SearchDealsQuery) => {
      if (!cache.searchDeals) {
        return cache
      }

      return {
        ...cache,
        searchDeals: {
          ...cache.searchDeals,
          deals: cache.searchDeals.deals.filter((deal) => {
            return !ids.includes(deal._id)
          }),
          total_rows: Math.max(0, (cache.searchDeals.total_rows || 0) - ids.length)
        }
      }
    })
  }
  const { total_rows = 0, deals = [] } = data?.searchDeals ?? {}
  // console.log('deals', deals)

  const handleRequestSort = (ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => {
    const newSort = `${direction === 'desc' ? '-' : ''}${name}`

    setPage(1)
    setUrlQuery({ location, params: { sort: newSort } })
    setSort(newSort)

    search({
      variables: {
        query: handlePipelineQuery({ pipeline, query: queryText, filters }),
        sort: getSort(newSort, queryState),
        limit
      }
    })
  }

  return (
    <>
      <DealSearchBar
        view={'list'}
        loading={loading}
        filters={filters}
        pipeline={pipeline}
        hasStage={true}
        onSearch={(text, filters) => {
          setQueryText(text)
          const query = handlePipelineQuery({ pipeline, filters, query: text })
          setQueryState(query)
          setFilters(filters)
          setPage(1)
          search({
            variables: {
              query,
              sort: getSort(sort, query),
              limit
            }
          })
        }}
      />

      <DealTable
        classes={{ dealTableCard: classes.dealTableCard }}
        skipRefetchQueries
        checkboxes={permissionToEditAllDeals}
        actions={{
          delete: true,
          archive: true,
          unarchive: true
        }}
        actionsForRow={(row) => ({
          delete: true,
          edit: true,
          copy: true,
          archive: !row.archived,
          unarchive: row.archived
        })}
        loading={loading}
        deals={deals}
        page={page}
        pages={total_rows < limit ? 1 : Math.min(Math.ceil(total_rows / limit), 20)}
        totalRows={total_rows}
        rowsPerPage={limit}
        sort={sort}
        onRequestSort={handleRequestSort}
        onPageChange={setPage}
        onFetchNextPage={() => {
          fetchMore({
            variables: { query: queryState, sort: getSort(sort, queryState), limit, bookmark },
            updateQuery: (previousResult: SearchDealsQuery, { fetchMoreResult }) => {
              if (!fetchMoreResult || !fetchMoreResult.searchDeals) {
                return previousResult
              }

              return {
                ...previousResult,
                ...fetchMoreResult,
                searchDeals: {
                  ...previousResult.searchDeals,
                  ...fetchMoreResult.searchDeals,
                  bookmark: fetchMoreResult.searchDeals.bookmark,
                  deals: [...previousResult.searchDeals.deals, ...fetchMoreResult.searchDeals.deals]
                }
              }
            }
          })
        }}
        onDelete={(ids) => {
          removeRowsFromQuery(ids)
        }}
        onArchive={(ids) => {
          removeRowsFromQuery(ids)
        }}
      />
    </>
  )
}

const PIPELINE_VALUE_ID = 'pipeline.value'

export function handlePipelineQuery(args: { pipeline: string; filters: ValueFilter[]; query: string | null }) {
  const { pipeline, filters, query } = args
  const filtersWithPipeline =
    pipeline === 'all'
      ? [...filters.filter((filter) => filter.id !== PIPELINE_VALUE_ID)]
      : [
          ...filters.filter((filter) => filter.id !== PIPELINE_VALUE_ID),
          {
            id: PIPELINE_VALUE_ID,
            type: 'in',
            value: [pipeline]
          } as ValueFilter
        ]

  const lucene =
    jsonToLucene({
      query: query ?? '',
      filters: filtersWithPipeline
    }) || '*:*'
  return lucene
}
